import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarRange, Scissors, Users, Settings, MessageCircle, CreditCard, IdCard,
  Package, Repeat, LogOut, DoorOpen, Boxes, Tag, Megaphone, TrendingUp, Banknote, Gift, BadgeCheck, ScrollText, Wallet,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const links = [
  { to: "/business", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/business/calendar", label: "Calendar", icon: CalendarRange },
  { to: "/business/clients", label: "Clients", icon: IdCard },
  { to: "/business/services", label: "Services", icon: Scissors },
  { to: "/business/payments", label: "Payments to collect", icon: Banknote },
  { to: "/business/wallet", label: "Wallet", icon: Wallet },
  { to: "/business/packages", label: "Packages", icon: Package },
  { to: "/business/memberships", label: "Memberships", icon: Repeat },
  { to: "/business/gift-cards", label: "Gift cards", icon: Gift },
  { to: "/business/promotions", label: "Promotions", icon: Tag },
  { to: "/business/marketing", label: "Marketing", icon: Megaphone },
  { to: "/business/staff", label: "Staff", icon: Users },
  { to: "/business/staff-performance", label: "Staff performance", icon: TrendingUp },
  { to: "/business/resources", label: "Resources", icon: DoorOpen },
  { to: "/business/inventory", label: "Inventory", icon: Boxes },
  { to: "/business/inbox", label: "WhatsApp inbox", icon: MessageCircle },
  { to: "/business/loyalty", label: "Loyalty program", icon: CreditCard },
  { to: "/business/subscription", label: "Plan & billing", icon: BadgeCheck },
  { to: "/business/settings", label: "Settings", icon: Settings },
  { to: "/legal/business-guidelines", label: "Key guidelines", icon: ScrollText },
];

export default function BusinessLayout() {
  const { user, loading, logout } = useAuth();
  const [business, setBusiness] = useState(null);
  const [hasBusiness, setHasBusiness] = useState(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!user || user.role !== "business_owner") return;
    api
      .get("/owner/business")
      .then(({ data }) => {
        setBusiness(data);
        setHasBusiness(true);
      })
      .catch((err) => setHasBusiness(err?.response?.status === 404 ? false : true));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/business" replace />;
  if (user.role !== "business_owner") return <Navigate to="/" replace />;
  if (hasBusiness === null) return null;
  if (hasBusiness === false) return <Navigate to="/business/onboarding" replace />;

  const isFreelance = business?.business_type === "freelance";
  const staffOnlyLinks = ["/business/staff", "/business/staff-performance"];
  const visibleLinks = links.filter((link) => !isFreelance || !staffOnlyLinks.includes(link.to));

  return (
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 py-4 sm:gap-8 sm:px-6 sm:py-8 md:grid-cols-[220px_1fr]">
      <nav className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 pb-2 sm:mx-0 sm:flex-col sm:overflow-visible sm:border-0 sm:px-0 sm:pb-0 sm:space-y-1">
        <p className="mb-3 hidden px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:block">
          Business
        </p>
        {visibleLinks.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium whitespace-nowrap ${
                isActive ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-muted"
              }`
            }
          >
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={handleLogout}
          className="flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-muted whitespace-nowrap sm:w-full"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </nav>
      <div className="min-w-0">
        <Outlet context={{ business }} />
      </div>
    </div>
  );
}
